// @flow

/**
 * Table Display Component
 * Renders a sortable, styled table with CSV export
 */

import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import DisplayWrapper from '../shared/DisplayWrapper';
import DisplayTitle from '../shared/DisplayTitle';
import ExportCsvButton from '../shared/ExportCsvButton';
import { formatValue } from '../shared/formatters';

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  color: white;
  background: var(--primary, #137cbd);
  border: 1px solid var(--primary, #137cbd);
  white-space: nowrap;
  cursor: ${props => props.$sortable ? 'pointer' : 'default'};
  user-select: none;

  &:hover {
    ${props => props.$sortable ? 'opacity: 0.9;' : ''}
  }
`;

const SortIndicator = styled.span`
  margin-left: 0.3rem;
  font-size: 0.7rem;
`;

const Td = styled.td`
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-weight: ${props => props.$bold ? '700' : 'normal'};
`;

const Tr = styled.tr`
  &:nth-child(even) {
    background: var(--card-background, #f9fafb);
  }
`;

type ColumnDef = {
  key: string,
  header: string,
  valueType: string,
  sortable?: boolean,
  highlightRules?: {
    boldWhen?: 'max' | 'min' | 'positive' | 'negative',
  },
};

type Props = {
  definition: {
    title: string,
    columns: ColumnDef[],
    rows: Object[],
  },
};

type SortState = {
  key: string | null,
  direction: 'asc' | 'desc' | null,
};

const NUMERIC_TYPES = new Set(['number', 'percentage', 'peopleCount', 'seconds', 'rate']);

function isNumericType(valueType: string): boolean {
  return NUMERIC_TYPES.has(valueType) || valueType.startsWith('currency-');
}

const TableDisplay = ({ definition }: Props) => {
  const { title, columns, rows } = definition;
  const [sort, setSort] = useState<SortState>({ key: null, direction: null });

  const handleSort = useCallback((colKey: string, sortable?: boolean) => {
    if (sortable === false) return;
    setSort(prev => {
      if (prev.key !== colKey) return { key: colKey, direction: 'asc' };
      if (prev.direction === 'asc') return { key: colKey, direction: 'desc' };
      return { key: null, direction: null };
    });
  }, []);

  // Pre-compute highlight values per column
  const highlights = useMemo(() => {
    const result = {};
    for (const col of columns) {
      if (!col.highlightRules?.boldWhen) continue;
      const numericValues = rows
        .map(r => r[col.key])
        .filter(v => v !== null && v !== undefined && typeof v === 'number');
      if (numericValues.length === 0) continue;

      const rule = col.highlightRules.boldWhen;
      if (rule === 'max') result[col.key] = Math.max(...numericValues);
      else if (rule === 'min') result[col.key] = Math.min(...numericValues);
      else result[col.key] = rule; // 'positive' or 'negative' - checked per-cell
    }
    return result;
  }, [columns, rows]);

  const shouldBold = useCallback((col: ColumnDef, value: any): boolean => {
    if (!col.highlightRules?.boldWhen) return false;
    const rule = col.highlightRules.boldWhen;
    if (rule === 'positive') return typeof value === 'number' && value > 0;
    if (rule === 'negative') return typeof value === 'number' && value < 0;
    return value === highlights[col.key];
  }, [highlights]);

  const sortedRows = useMemo(() => {
    if (!sort.key || !sort.direction) return rows;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return rows;

    const isNumeric = isNumericType(col.valueType);
    const dir = sort.direction === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      const aVal = a[sort.key];
      const bVal = b[sort.key];

      // Nulls always to bottom
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (isNumeric) {
        return (Number(aVal) - Number(bVal)) * dir;
      }
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }, [rows, sort, columns]);

  return (
    <DisplayWrapper>
      <TableHeader>
        <DisplayTitle style={{ marginBottom: 0 }}>{title}</DisplayTitle>
        <ExportCsvButton title={title} columns={columns} rows={rows} />
      </TableHeader>
      <ScrollContainer>
        <StyledTable>
          <thead>
            <tr>
              {columns.map(col => (
                <Th
                  key={col.key}
                  $sortable={col.sortable !== false}
                  onClick={() => handleSort(col.key, col.sortable)}
                >
                  {col.header}
                  {sort.key === col.key && sort.direction && (
                    <SortIndicator>
                      {sort.direction === 'asc' ? '\u25B2' : '\u25BC'}
                    </SortIndicator>
                  )}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, rowIdx) => (
              <Tr key={rowIdx}>
                {columns.map(col => (
                  <Td key={col.key} $bold={shouldBold(col, row[col.key])}>
                    {formatValue(row[col.key], col.valueType)}
                  </Td>
                ))}
              </Tr>
            ))}
          </tbody>
        </StyledTable>
      </ScrollContainer>
    </DisplayWrapper>
  );
};

export default TableDisplay;
